import PropTypes from 'prop-types'
import styled from 'styled-components'
import { customPropTypes } from 'tools/react'
import breakpoint from 'tools/styled-components-breakpoint'
import { darken } from 'tools/style'
import Div from 'components/Div'
import { typo, margin, marginSizes, colors } from 'theme/default'

const fontWeights = typo.fontWeights

const commonPropTypes = {
  mobileFontSize: PropTypes.string,
  mobileLineHeight: PropTypes.string,
  tabletFontSize: PropTypes.string,
  tabletLineHeight: PropTypes.string,
  desktopFontSize: PropTypes.string,
  desktopLineHeight: PropTypes.string,
  color: PropTypes.string,
  fontWeight: customPropTypes.oneOfObjectKeys(fontWeights),
  margin: customPropTypes.oneOfObjectKeys(marginSizes),
  marginTop: customPropTypes.oneOfObjectKeys(marginSizes),
  marginBottom: customPropTypes.oneOfObjectKeys(marginSizes),
  marginLeft: customPropTypes.oneOfObjectKeys(marginSizes),
  marginRight: customPropTypes.oneOfObjectKeys(marginSizes),
  normalised: PropTypes.bool,
  bold: PropTypes.bool
}

const headerPropTypes = {
  ...commonPropTypes
}

export const Text = styled(Div).attrs(() => ({ as: 'span' }))`
  text-transform: ${props => props.textTransform};
  font-size: ${props => props.mobileFontSize};
  line-height: ${props => props.mobileLineHeight};
  font-weight: ${props => props.bold ? fontWeights.bold : fontWeights.regular};
  font-style: ${props => props.italic ? 'italic' : 'normal'};
  ${props => props.underline ? `text-decoration: underline;` : ''}
  font-family: ${props => props.fontFamily || props.theme.typographyBodyText};
  color: ${props => props.color ? props.color : props.theme.colorBodyFont};
  white-space: ${props => props.whiteSpace || 'pre-wrap'};
  max-width: 100%;
  ${props => props.textAlign ? `
    width: 100%;
    text-align: ${props.textAlign};
  ` : ''}
  ${props => props.normalised ? 'margin: 0;' : ''}
  ${props => props.margin ? `margin: ${margin[props.margin]};` : ''}
  ${props => props.marginTop ? `margin-top: ${margin[props.marginTop]};` : ''}
  ${props => props.marginBottom ? `margin-bottom: ${margin[props.marginBottom]};` : ''}
  ${props => props.marginLeft ? `margin-left: ${margin[props.marginLeft]};` : ''}
  ${props => props.marginRight ? `margin-right: ${margin[props.marginRight]};` : ''}
  ${props => props.line !== undefined ? `
    overflow: hidden;
    text-overflow: ellipsis;
    ${props.flexHeight ? 'max-height' : 'height'}: calc(${props.mobileLineHeight} * ${props.line});
    display: -webkit-box;
    -webkit-line-clamp: ${props.line};
    -webkit-box-orient: vertical;` : ''}
  ${breakpoint('tablet')`
    font-size: ${props => props.tabletFontSize};
    line-height: ${props => props.tabletLineHeight};
    ${props => props.line !== undefined ? `
    ${props.flexHeight ? 'max-height' : 'height'}: calc(${props.tabletLineHeight} * ${props.line});` : ''}
  `}
  ${breakpoint('desktop')`
    font-size: ${props => props.desktopFontSize};
    line-height: ${props => props.desktopLineHeight};
    ${props => props.line !== undefined ? `
    ${props.flexHeight ? 'max-height' : 'height'}: calc(${props.desktopLineHeight} * ${props.line});` : ''}
  `}
`

const Header = styled(Text)`
  color: ${props => props.color || (props.sectionHeading ? props.theme.colorSectionHeadingsFont : props.theme.colorHeadingsFont)};
  font-weight: ${props => props.bold ? fontWeights.bold : (props.sectionHeading ? fontWeights.bold : fontWeights.regular)};
  && {
    font-family: ${props => props.fontFamily || props.theme.typographyHeadings};
  }
`

export const MenuNameHeader = styled(Header).attrs(() => ({ as: 'h1' }))``
MenuNameHeader.propTypes = headerPropTypes
MenuNameHeader.defaultProps = typo.h1

export const H1 = styled(Header).attrs(() => ({ as: 'h2' }))``
H1.propTypes = headerPropTypes
H1.defaultProps = typo.h1

export const H2 = styled(Header).attrs(() => ({ as: 'h2' }))``
H2.propTypes = headerPropTypes
H2.defaultProps = typo.h2

export const H3 = styled(Header).attrs(() => ({ as: 'h3' }))``
H3.propTypes = headerPropTypes
H3.defaultProps = typo.h3

export const H4 = styled(Header).attrs(() => ({ as: 'h4' }))``
H4.propTypes = headerPropTypes
H4.defaultProps = typo.h4

export const P = styled(Text).attrs(() => ({ as: 'p' }))``
P.propTypes = commonPropTypes
P.defaultProps = typo.p

export const SmallText = styled(Text).attrs(() => ({ as: 'p' }))``
SmallText.props = commonPropTypes
SmallText.defaultProps = typo.smallText

export const LinkText = styled(Text).attrs(() => ({
  tabIndex: 0
}))`
  text-decoration: underline;
  cursor: pointer;
  color: ${props => props.color ? props.color : (props.heading ? props.theme.colorHeadingsFont : colors.brandPrimary)};

  &:hover {
    color: ${props => props.color ? darken(props.color) : (props.heading ? darken(props.theme.colorHeadingsFont) : colors.brandPrimaryDarkest)};
  }
`
LinkText.defaultProps = {
  theme: {
    colorHeadingsFont: colors.headings
  }
}
